import React, { useEffect, useState } from 'react';
import { CameraIcon, CheckIcon, DownloadIcon, MessageCircleIcon } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { useLocale } from '../../contexts/LocaleContext';
type Target = 'stories' | 'whatsapp' | 'save';

/**
 * Reusable share destination sheet. Sharing is always a user action;
 * the prototype stops at "image ready" — the OS share sheet is REQUIRES DEVICE.
 */
export function ShareSheet({
  open,
  onClose,
  onShared
}: {
  open: boolean;
  onClose: () => void;
  /** Prototype share-intent event. Production must use a verifiable platform event when available. */
  onShared?: (target: Target) => void;
}) {
  const {
    t
  } = useLocale();
  const [done, setDone] = useState<Target | null>(null);
  useEffect(() => {
    if (!open) setDone(null);
  }, [open]);
  const targets: {
    id: Target;
    label: string;
    Icon: typeof CameraIcon;
  }[] = [{
    id: 'stories',
    label: t('eng.share.stories'),
    Icon: CameraIcon
  }, {
    id: 'whatsapp',
    label: t('eng.share.whatsapp'),
    Icon: MessageCircleIcon
  }, {
    id: 'save',
    label: t('eng.share.save'),
    Icon: DownloadIcon
  }];
  const chosen = targets.find(target => target.id === done);
  return <Modal open={open} onClose={onClose} labelledBy="share-sheet-title" data-id="element-717" data-mp="1ewexdihx3yda">
      <h2 id="share-sheet-title" className="pr-5 text-section text-deep" data-id="element-718" data-mp="1jwubica0x9wb">
        {done ? t('eng.share.ready') : t('eng.share.sheet')}
      </h2>
      {chosen ? <div className="mt-2" data-id="element-719" data-mp="1nw1y022tp05a">
          <p className="flex items-start gap-2 text-[13px] leading-relaxed text-muted" data-id="element-720" data-mp="1swheh2ieinrh">
            <CheckIcon size={16} className="mt-[2px] shrink-0 text-gold-deep" aria-hidden="true" data-id="element-721" data-mp="1xnz9l9xqtu4j" />
            {t('eng.share.readyBody', {
          target: chosen.label
        })}
          </p>
          <Button variant="primary" size="lg" fullWidth className="mt-3" onClick={onClose} data-id="element-722" data-mp="22on0zwpvwcza">
            {t('common.close')}
          </Button>
        </div> : <>
          <p className="mt-1 text-[12px] text-muted" data-id="element-723" data-mp="2bnqordzzxgk4">{t('eng.share.format')}</p>
          <ul className="mt-2 space-y-1" data-id="element-724" data-mp="cjid68wso5x5">
            {targets.map(({
          id,
          label,
          Icon
        }) => <li key={id} data-id="element-725" data-mp="7kxbs65t3g74">
                <button type="button" onClick={() => {
            setDone(id);
            onShared?.(id);
          }} className="flex min-h-[52px] w-full items-center gap-2 rounded-card border border-sand-line bg-white/70 px-2 text-left transition-colors duration-150 ease-brand hover:border-deep/25" data-id="element-726" data-mp="7hjdpa46dt5h">
                  <span className="grid h-9 w-9 place-items-center rounded-[10px] bg-sand-deep text-deep" data-id="element-727" data-mp="qc82am7mdqyh">
                    <Icon size={17} aria-hidden="true" data-id="element-728" data-mp="147k7xonbd1u6" />
                  </span>
                  <span className="text-[13.5px] font-medium text-deep" data-id="element-729" data-mp="109335brd83fx">{label}</span>
                </button>
              </li>)}
          </ul>
        </>}
    </Modal>;
}